import * as React from "react";

import { css } from "styled-components";

export interface ModuleSectionData {
  href: string;
  title: string;
  type: string;
}

export interface SectionModuleProps {
  sectionTitle: string;
  sectionNumber: number;
  sectionData?: ModuleSectionData[];
}

interface BaseStyledSectionProps extends SectionModuleProps {
  id: string;
  fluid?: boolean;
}

type SectionProps = React.ComponentPropsWithoutRef<"button"> &
  BaseStyledSectionProps;

type IntersectionSectionValues = SectionModuleProps;

export const ModuleSection = React.forwardRef<HTMLElement, SectionProps>(
  (
    { fluid, sectionTitle, sectionNumber, sectionData, children, ...rest },
    ref,
  ) => {
    const { observerRef, refList } = useModuleRefs();

    const $ref = React.useRef<HTMLElement | null>(null);

    React.useEffect(() => {
      refList.current.set($ref.current!, {
        sectionTitle,
        sectionNumber,
        sectionData,
      });
      observerRef.current.observe($ref.current!);
    }, [sectionTitle]);

    return (
      <section
        css={`
          ${fluid &&
          css`
            margin-left: -2rem;
            margin-right: -2rem;
          `}
        `}
        {...rest}
        ref={(r) => {
          ($ref as React.MutableRefObject<HTMLElement>).current = r as HTMLElement;
          if (typeof ref === "function") {
            ref(r);
          } else if (ref) {
            ref.current = r;
          }
        }}
      >
        {children}
      </section>
    );
  },
);

type ModuleStateContext = {
  observerRef: React.MutableRefObject<IntersectionObserver>;
  refList: React.MutableRefObject<Map<Element, IntersectionSectionValues>>;
};

const ModuleStateContext = React.createContext<ModuleStateContext | undefined>(
  undefined,
);

interface ModuleProviderProps {
  onSectionChange(values: IntersectionSectionValues | undefined): void;
}
export const ModuleProvider: React.FC<ModuleProviderProps> = ({
  children,
  onSectionChange,
}) => {
  const refList = React.useRef(new Map<Element, IntersectionSectionValues>());

  const threshholdRef = React.useRef(new Map<Element, number>());

  const observerRef = React.useRef(
    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target;
          threshholdRef.current.set(id, entry.intersectionRatio);
        });

        let largestValue = 0;
        let largestId;

        threshholdRef.current.forEach((v, k) => {
          if (v > largestValue) {
            largestValue = v;
            largestId = k;
          }
        });

        if (largestValue && largestId) {
          onSectionChange(refList.current.get(largestId));
        }
      },
      {
        rootMargin: "0px",
        threshold: [0, 0.2, 0.8, 1],
      },
    ),
  );

  return (
    <ModuleStateContext.Provider value={{ refList, observerRef }}>
      {children}
    </ModuleStateContext.Provider>
  );
};

export const useModuleRefs = () => {
  const ctx = React.useContext(ModuleStateContext);

  if (!ctx) {
    throw Error("useModuleObserver used outside ModuleProvider");
  }

  return ctx;
};

interface ModuleState {
  currentSection: number;
  currentSectionTitle: string;
  currentSectionData?: ModuleSectionData[] | undefined;
}

export const useModuleState = (
  defaultState: ModuleState = {
    currentSection: 1,
    currentSectionTitle: "Introduction",
    currentSectionData: undefined,
  },
) => {
  const [state, setState] = React.useState<ModuleState>(defaultState);

  const methods = React.useMemo(() => {
    return {
      handleSectionChange: (section: SectionModuleProps) =>
        setState((prev) => ({
          ...prev,
          currentSectionTitle: section!.sectionTitle,
          currentSection: section!.sectionNumber,
          currentSectionData: section!.sectionData,
        })),
    };
  }, [setState]);

  return [state, setState, methods] as const;
};
